import { NoticeDetails } from "lib/hooks/api/notices/useNotice";
import { FrameworkProcessType } from "lib/types/graphQLEnums";

export type RelationshipOrg = { guid: string; name: string };

type NoticeParties = Pick<NoticeDetails, "stage" | "buyers" | "relatedFrameworkProcess">;
type IncumbentsNotice = NoticeParties & Pick<NoticeDetails, "lots" | "suppliers">;

/**
 * The one buyer whose relationships with the notice's suppliers are clear, or null when they're
 * ambiguous: no award yet, several buyers (any of them could be the payer), or a framework
 * agreement, whose named buyer is usually a purchasing body that never pays the supplier itself.
 */
export function getRelationshipBuyer(notice: NoticeParties): RelationshipOrg | null {
  if (notice.stage !== "AWARDED" || notice.buyers.length !== 1) {
    return null;
  }
  if (notice.relatedFrameworkProcess?.processType === FrameworkProcessType.Framework) {
    return null;
  }
  const [{ guid, name }] = notice.buyers;
  return guid ? { guid, name } : null;
}

/**
 * The buyer an award's suppliers are paired with: the clear one, or on a joint award the lead
 * (first-listed) buyer. Null before an award, on a framework agreement, or with no buyer guid.
 */
export function getAwardPairBuyer(notice: NoticeParties): RelationshipOrg | null {
  if (notice.stage !== "AWARDED" || notice.buyers.length === 0) return null;
  if (notice.relatedFrameworkProcess?.processType === FrameworkProcessType.Framework) return null;
  const [{ guid, name }] = notice.buyers;
  return guid ? { guid, name } : null;
}

/**
 * Whether the notice has a Potential incumbents section to show: not for framework agreements
 * (the buyer they name usually never pays the supplier), a tender needs award data to see who won
 * the buyer's past contracts, and an award needs a buyer to pair with a supplier.
 */
export function showsIncumbents(notice: IncumbentsNotice, hasAwardData: boolean): boolean {
  if (notice.buyers.length === 0) return false;
  if (notice.relatedFrameworkProcess?.processType === FrameworkProcessType.Framework) return false;
  if (notice.stage !== "AWARDED") return hasAwardData;
  return getAwardPairBuyer(notice) !== null && noticeSuppliers(notice).length > 0;
}

/**
 * The notice's suppliers with a Stotles record, once each: those its lots name, in order, then any
 * named only on the notice itself, as some awards have no lots.
 */
export function noticeSuppliers(
  notice: Pick<NoticeDetails, "lots" | "suppliers">,
): RelationshipOrg[] {
  const seen = new Map<string, RelationshipOrg>();
  const named = [...notice.lots.flatMap((lot) => lot.suppliers ?? []), ...(notice.suppliers ?? [])];
  for (const supplier of named) {
    if (supplier.guid && !seen.has(supplier.guid)) {
      seen.set(supplier.guid, { guid: supplier.guid, name: supplier.name });
    }
  }
  return [...seen.values()];
}
